import React from "react";
import { Link } from "react-router-dom";
import { useUser } from "./Context/UserContext";
import Icon from "./ui/Icon";
import EmptyState from "./ui/EmptyState";
import "../styles/skillSetReports.css";

/*
 * SM-HR-009 / SM-GEN-003
 *
 * "Skill Set Reports" is a parent menu that groups the report pages, so the
 * server returns it with routePath: null. That left the menu item dead — QA
 * logged it as a broken link, and the child reports were only reachable by
 * typing their URLs.
 *
 * This is the landing page for that group: it opens, it loads, and it links to
 * every child report the signed-in user actually has access to.
 */

const DESCRIPTIONS = {
    "/AdminReport":
        "Every submitted skill record with manager evaluation, competency levels and training recommendations. Filterable and downloadable as Excel.",
    "/BenchReport":
        "Active bench resources with primary and secondary skills, total experience and days on bench.",
    "/AdminExtraFunctions":
        "Maintain skill domains, employee previous-experience records and bulk experience uploads.",
};

const ICONS = {
    "/AdminReport": "report",
    "/BenchReport": "briefcase",
    "/AdminExtraFunctions": "settings",
};

export default function SkillSetReports() {
    const { menus } = useUser();

    const safeMenus = menus || [];

    // The group this page stands in for, and the children the user may open.
    const parent = safeMenus.find(
        m => !m.parentMenuId && !m.routePath && m.menuName === "Skill Set Reports"
    );

    const children = safeMenus.filter(
        m =>
            m.routePath &&
            parent &&
            Number(m.parentMenuId) === Number(parent.menuId)
    );

    return (
        <div className="reports-hub">
            <header className="page-head reveal">
                <span className="page-eyebrow">Reports</span>
                <h1 className="page-title-mt">Skill Set Reports</h1>
                <p className="page-sub">
                    Skill, bench and administration reporting for the organisation.
                </p>
            </header>

            {children.length > 0 ? (
                <div className="reports-hub-grid">
                    {children.map((child, i) => (
                        <Link
                            key={child.menuId}
                            to={child.routePath}
                            className={`reports-hub-card card-mt card-pad reveal reveal-d${Math.min(i + 1, 6)}`}
                        >
                            <span className="reports-hub-icon" aria-hidden="true">
                                <Icon name={ICONS[child.routePath] || "report"} size={20} />
                            </span>

                            <span className="reports-hub-body">
                                <span className="reports-hub-title">{child.menuName}</span>
                                <span className="reports-hub-text">
                                    {DESCRIPTIONS[child.routePath] ||
                                        "Open this report."}
                                </span>
                            </span>

                            <span className="reports-hub-arrow" aria-hidden="true">
                                <Icon name="chevronRight" size={18} />
                            </span>
                        </Link>
                    ))}
                </div>
            ) : (
                <div className="card-mt card-pad reveal">
                    <EmptyState
                        icon="report"
                        title="No reports available"
                        text="Your role does not have access to any report under Skill Set Reports. Contact an administrator if you expect to see one here."
                    />
                </div>
            )}
        </div>
    );
}
